// observability — vendor-neutral facade för error-tracking + breadcrumbs.
//
// Bakgrund: 100-list-punkt #89 specade Sentry. Att ta in en
// tredjeparts-SDK (~30KB gz + ~128 transitiva paket + ny egress)
// är ett ops-beslut. Den här facaden ger samma fördelar UTAN att
// låsa in Sentry: alla call-sites på FE använder
// `observability.captureException` / `observability.addBreadcrumb`,
// och adaptern bakom routar idag till `logger`. Den dag DSN väljs
// blir bytet en 1-rads-swap i `setAdapter()`.
//
// **Designprinciper:**
//   1. Inga PII — call-sites loggar ALDRIG email/tokens/passwords.
//      Adapter får bara user-id (eller helt anonymt).
//   2. Adaptern bakom är BYTBAR (default `consoleLoggerAdapter`,
//      sätt en Sentry-adapter senare).
//   3. Breadcrumb-buffer på FE är minneslokal — utan adapter som
//      hostar dem ut, går de förlorade vid reload. Det är OK för
//      facade-nivån.
//   4. ALDRIG initiera adapter automatiskt med envvar — explicit
//      `setAdapter()`-anrop vid app-bootstrap. Hindrar
//      fork-spam när någon glömmer en DSN.

import { logger } from '@/lib/logger';

export type BreadcrumbCategory =
  | 'auth'
  | 'dialog'
  | 'edge-fn'
  | 'navigation'
  | 'custom';

export interface Breadcrumb {
  category: BreadcrumbCategory;
  /** Kort beskrivning. Får ALDRIG innehålla PII. */
  message: string;
  /** Extra strukturerad data. Får ALDRIG innehålla PII. */
  data?: Record<string, string | number | boolean | null>;
  /** Auto-satt om utelämnad. */
  timestamp?: number;
  level?: 'info' | 'warn' | 'error';
}

export interface ObservabilityAdapter {
  /** Inkommer för varje breadcrumb. */
  addBreadcrumb: (b: Breadcrumb) => void;
  /** Inkommer för varje uncaught + manuellt captured error. */
  captureException: (error: unknown, context?: { errorId?: string; tags?: Record<string, string> }) => void;
  /** Inkommer när auth-state byter (login/logout). user-id ONLY. */
  identifyUser: (userId: string | null) => void;
}

// ────────────────────────────────────────────────────────────────────
// Default adapter — routar till logger så breadcrumb-flödet syns i
// dev-konsolen utan extra dep. captureException loggar `error`-nivå
// med ett errorId-prefix om sådant finns.
// ────────────────────────────────────────────────────────────────────

const consoleLoggerAdapter: ObservabilityAdapter = {
  addBreadcrumb(b) {
    logger.debug(`[obs:${b.category}] ${b.message}`, b.data ?? {});
  },
  captureException(error, ctx) {
    const prefix = ctx?.errorId ? `[obs:capture ${ctx.errorId}]` : '[obs:capture]';
    logger.error(prefix, error, ctx?.tags ?? {});
  },
  identifyUser(userId) {
    logger.debug('[obs:identify]', { userId: userId ?? '<anonymous>' });
  },
};

let adapter: ObservabilityAdapter = consoleLoggerAdapter;

/**
 * Byt observability-adapter. Anropas EN gång vid bootstrap (main.tsx),
 * t.ex. när Sentry/Datadog väljs. Att swappa mid-flight är OK men
 * pågående breadcrumbs i den gamla adaptern återkommer inte.
 */
export function setAdapter(next: ObservabilityAdapter): void {
  adapter = next;
}

/** Återställ till default (test-only). */
export function resetAdapterForTests(): void {
  adapter = consoleLoggerAdapter;
}

// ────────────────────────────────────────────────────────────────────
// Public facade
// ────────────────────────────────────────────────────────────────────

export const observability = {
  addBreadcrumb(b: Omit<Breadcrumb, 'timestamp'> & { timestamp?: number }): void {
    adapter.addBreadcrumb({ ...b, timestamp: b.timestamp ?? Date.now() });
  },
  captureException(error: unknown, context?: { errorId?: string; tags?: Record<string, string> }): void {
    adapter.captureException(error, context);
  },
  identifyUser(userId: string | null): void {
    adapter.identifyUser(userId);
  },
};
